// src/components/RefineChatPanel.js — fixed right-edge chat panel for the
// content editor's agentic refine flow.
//
// Built on assistant-ui — handles streaming, autoscroll, accessibility,
// keyboard shortcuts. Runtime adapter (useRefineRuntime) connects it to
// our /content/refine API and applies returned markdown to the editor's
// textarea via the onApply callback.
//
// UX:
//   - Fixed to the right edge so it's reachable regardless of scroll
//   - Collapsed = thin "Refine" tab; expanded = 400px panel
//   - Editor reflows around the panel (var(--refine-panel-width))
//   - Open/closed state persists in localStorage
//   - ESC closes
import React, { useEffect, useState } from 'react';
import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { ChevronLeft, X } from 'lucide-react';

import Thread from './chat/Thread';
import useRefineRuntime from './chat/useRefineRuntime';

const STORAGE_KEY = 'refine-panel:open';
const PANEL_WIDTH = 400;  // px

export default function RefineChatPanel(props) {
  const { runtime, hasMessages } = useRefineRuntime(props);
  const [open, setOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === null) return true;
      return stored === 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(open)); } catch {}
  }, [open]);

  // Reserve viewport space when open so the editor reflows.
  useEffect(() => {
    document.body.style.setProperty(
      '--refine-panel-width',
      open ? `${PANEL_WIDTH}px` : '0px',
    );
    return () => { document.body.style.removeProperty('--refine-panel-width'); };
  }, [open]);

  // ESC to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      {/* Toggle tab */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close refine panel' : 'Open refine panel'}
        aria-expanded={open}
        className={`fixed top-1/2 -translate-y-1/2 z-30 flex items-center justify-center gap-2 rounded-l-md border border-r-0 transition-colors shadow-sm ${
          open
            ? 'h-20 w-7 border-line bg-surface text-mutedink hover:text-ink hover:bg-surface-muted'
            : 'h-32 w-9 border-accent-subtle bg-accent text-[var(--on-accent)] hover:bg-accent-hover'
        }`}
        style={{ right: open ? `${PANEL_WIDTH}px` : 0 }}
      >
        {open ? (
          <ChevronLeft className="w-4 h-4 rotate-180" />
        ) : (
          <span className="flex flex-col items-center gap-1.5">
            <ChevronLeft className="w-4 h-4" />
            <span
              className="text-[11px] font-medium tracking-wide"
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              Refine
            </span>
          </span>
        )}
      </button>

      {/* Slide-in panel */}
      <aside
        aria-hidden={!open}
        className="fixed top-[65px] right-0 bottom-0 z-20 bg-surface border-l border-line shadow-lg transition-transform duration-200 ease-out flex flex-col"
        style={{
          width: `${PANEL_WIDTH}px`,
          transform: open ? 'translateX(0)' : `translateX(${PANEL_WIDTH}px)`,
        }}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-line">
          <h2 className="text-sm font-medium text-ink">Refine with AI</h2>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="p-1 text-faint hover:text-read transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* The chat fills the rest of the panel. assistant-ui handles
            streaming, autoscroll, autosizing input, ESC-on-empty, etc. */}
        <div className="flex-1 min-h-0">
          <AssistantRuntimeProvider runtime={runtime}>
            <Thread
              emptyState={
                !hasMessages && (
                  <p className="text-xs text-mutedink px-4 pt-4 leading-relaxed">
                    Tell me how to change this content. Each request is
                    independent — I read your current draft, apply your
                    instruction, and the editor updates. You can keep iterating.
                  </p>
                )
              }
            />
          </AssistantRuntimeProvider>
        </div>
      </aside>
    </>
  );
}
